// Voice editor — analyse-from-history progress toast (US-097).
//
// The prototype renders a fixed toast at `prototypes_and_docs/ls-voice.jsx:252-272`
// with a spinner + "Analyse en cours… Ns" counter that updates every 250ms.
// We re-implement that behaviour over the DS `Toast` primitive (loading tone),
// using a self-contained timer so the parent only needs to flip the `active`
// prop.
//
// Once the Wave 6 workflow streams real WS progress events the parent can
// pass the elapsed counter explicitly via `elapsedMs`; today the toast just
// counts wall-clock seconds since `start`.

import { useEffect, useState } from 'react';

import { useT } from '@barel/i18n';
import { Toast } from '@barel/ui';

export interface AnalysisProgressToastProps {
  /** When false, the toast is unmounted. */
  active: boolean;
  /** Optional explicit elapsed-ms override (used by tests + WS streaming). */
  elapsedMs?: number;
}

export function AnalysisProgressToast({ active, elapsedMs }: AnalysisProgressToastProps) {
  const t = useT('voice');
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return undefined;
    }
    if (elapsedMs !== undefined) {
      setSeconds(Math.floor(elapsedMs / 1000));
      return undefined;
    }
    const start = Date.now();
    setSeconds(0);
    const handle = window.setInterval(() => {
      setSeconds(Math.floor((Date.now() - start) / 1000));
    }, 250);
    return () => {
      window.clearInterval(handle);
    };
  }, [active, elapsedMs]);

  if (!active) return null;

  const description =
    seconds === 0 ? t('history.progressBodyZero') : t('history.progressBody', { seconds });

  return (
    <Toast
      tone="loading"
      title={t('history.progressTitle')}
      description={description}
      duration={Number.POSITIVE_INFINITY}
      dismissible={false}
      data-testid="voice-analysis-progress"
    />
  );
}
